import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import {
    listMFAFactors,
    unenrollMFA,
} from "../services/mfaService";
import { getSupabaseClient } from "../services/supabaseClient";
import MFAEnrollment from "./MFAEnrollment";

const formatDateTime = (value) => {
    if (!value) {
        return "Not enrolled";
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return "Not enrolled";
    }
    return parsed.toLocaleString();
};

export default function MFAManagement() {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [profile, setProfile] = useState(null);
    const [error, setError] = useState(null);
    const [showEnrollment, setShowEnrollment] = useState(false);
    const [disableModalOpen, setDisableModalOpen] = useState(false);
    const [disablePassword, setDisablePassword] = useState("");
    const [processing, setProcessing] = useState(false);

    const isEnabled = Boolean(profile?.mfa_enabled);
    const enrolledAt = useMemo(
        () => formatDateTime(profile?.mfa_enrolled_at),
        [profile]
    );

    useEffect(() => {
        const loadProfile = async () => {
            if (!user) {
                setLoading(false);
                return;
            }

            setLoading(true);
            setError(null);
            const supabase = getSupabaseClient();
            const { data, error: profileError } = await supabase
                .from("profiles")
                .select("mfa_enabled, mfa_enrolled_at")
                .eq("id", user.id)
                .maybeSingle();

            if (profileError) {
                setError(profileError.message);
                setLoading(false);
                return;
            }

            setProfile(data || { mfa_enabled: false, mfa_enrolled_at: null });
            setLoading(false);
        };

        loadProfile().catch(() => {
            setError("Unable to load MFA status.");
            setLoading(false);
        });
    }, [user]);

    const handleDisable = async () => {
        if (!user) {
            return;
        }

        if (!disablePassword) {
            setError("Enter your password to continue.");
            return;
        }

        setProcessing(true);
        setError(null);

        try {
            const supabase = getSupabaseClient();
            if (typeof supabase.auth.reauthenticate === "function") {
                const { error: reauthError } = await supabase.auth.reauthenticate({
                    password: disablePassword,
                });
                if (reauthError) {
                    throw reauthError;
                }
            } else {
                const { error: reauthError } = await supabase.auth.signInWithPassword({
                    email: user.email,
                    password: disablePassword,
                });
                if (reauthError) {
                    throw reauthError;
                }
            }

            const factors = await listMFAFactors();
            const list = factors?.totp || factors?.all || factors?.factors || [];
            for (const factor of list) {
                if (factor?.id) {
                    await unenrollMFA({ factorId: factor.id });
                }
            }

            const { error: profileError } = await supabase
                .from("profiles")
                .update({ mfa_enabled: false, mfa_enrolled_at: null })
                .eq("id", user.id);

            if (profileError) {
                throw profileError;
            }

            setProfile({ mfa_enabled: false, mfa_enrolled_at: null });
            setDisableModalOpen(false);
            setDisablePassword("");
        } catch (err) {
            setError(err?.message || "Unable to disable MFA.");
        } finally {
            setProcessing(false);
        }
    };

    if (loading) {
        return (
            <div className="profile-card">
                <h2>Two-Factor Authentication</h2>
                <p>Loading MFA status...</p>
            </div>
        );
    }

    return (
        <div className="profile-card">
            <div className="profile-card-header">
                <div>
                    <h2>Two-Factor Authentication</h2>
                    <p className="text-muted">
                        Protect your account with an authenticator app.
                    </p>
                </div>
                <div className={`status-pill ${isEnabled ? "enabled" : "disabled"}`}>
                    {isEnabled ? "Enabled" : "Disabled"}
                </div>
            </div>

            <div className="profile-grid">
                <div>
                    <span className="field-label">Enrollment date</span>
                    <p>{enrolledAt}</p>
                </div>
            </div>

            {error && <p className="error-message">{error}</p>}

            {!isEnabled && !showEnrollment && (
                <button
                    className="btn primary"
                    type="button"
                    onClick={() => setShowEnrollment(true)}
                >
                    Enable MFA
                </button>
            )}

            {isEnabled && (
                <button
                    className="btn ghost"
                    type="button"
                    onClick={() => setDisableModalOpen(true)}
                >
                    Disable MFA
                </button>
            )}

            {showEnrollment && (
                <MFAEnrollment
                    userId={user?.id}
                    onEnrolled={() => {
                        setProfile({
                            mfa_enabled: true,
                            mfa_enrolled_at: new Date().toISOString(),
                        });
                        setShowEnrollment(false);
                    }}
                    onCancel={() => setShowEnrollment(false)}
                />
            )}

            {disableModalOpen && (
                <div className="modal-root">
                    <div className="modal-overlay" />
                    <div className="modal-card">
                        <div className="modal-header">
                            <h3>Disable MFA</h3>
                        </div>
                        <p className="modal-body">
                            Enter your password to confirm disabling MFA.
                        </p>
                        <label className="form-field">
                            <span>Password</span>
                            <input
                                type="password"
                                autoComplete="current-password"
                                value={disablePassword}
                                onChange={(e) => setDisablePassword(e.target.value)}
                            />
                        </label>
                        <div className="modal-actions">
                            <button
                                className="btn ghost"
                                type="button"
                                onClick={() => {
                                    setDisableModalOpen(false);
                                    setDisablePassword("");
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn primary"
                                type="button"
                                onClick={handleDisable}
                                disabled={processing}
                            >
                                {processing ? "Disabling..." : "Disable MFA"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
